import { useState, ChangeEvent } from 'react';
import { AutomateError, deleteConnectorToken } from '../api';
import { useApp } from '../common/AppProvider';
import { AutomationTask, Connector } from '../tasks/types';
import { translations } from '../translations/translations';
import AuthenticatedConnector from './AuthenticatedConnector';
import { ExInput } from '@boomi/exosphere';
import ItemList from '../common/ItemList';

interface ConnectorTask {
    id: string;
    displayName: string;
}

export interface ConnectorWithTasks extends Connector {
    tasks: ConnectorTask[];
}

// Figure out which tasks are related to the specified connector via the triggers or actions.
const getConnectorTasks = (connector: Connector, tasks: AutomationTask[]): ConnectorTask[] => {
    const connectorTriggerIds: string[] = Object.keys(connector.triggers);
    const connectorActionsIds: string[] = Object.keys(connector.actions);
    const connectorTasks: (ConnectorTask | null)[] = tasks.map((task) =>
        (task.trigger?.id && connectorTriggerIds.includes(task.trigger.id)) ||
        task.actions.some((a) => connectorActionsIds.includes(a.id))
            ? {
                  displayName: task.displayName,
                  id: task.id,
              }
            : null,
    );

    // TS complains that I'm returning (ConnectorTask | null)[] but I'm filtering the nulls out...
    return connectorTasks.filter((item) => item) as ConnectorTask[];
};

const Connections = () => {
    const { language, connectors, tasks, syncAndUpdate, alertError, setIsLoading } = useApp();
    const [search, setSearch] = useState('');

    const getAuthenticatedConnectors = (connectorList: Connector[]) => {
        return connectorList.filter((connector) => {
            const connectorType = connector.$t.toUpperCase();

            if ((connectorType === 'OAUTH' && connector.authorizedScopes?.length) ?? 0 > 0)
                return true;
            if (
                (connectorType === 'APIKEY' ||
                    connectorType === 'BASIC' ||
                    connectorType === 'CUSTOM') &&
                connector.authorized
            )
                return true;

            return false;
        });
    };

    const authenticatedConnectors = getAuthenticatedConnectors(Object.values(connectors)).filter(
        (connector) => connector.displayName.toUpperCase().includes(search.toUpperCase()),
    );

    // Augment the connectors with their related tasks.
    const authenticatedConnectorsWithTasks: ConnectorWithTasks[] = authenticatedConnectors.map(
        (connector) => ({
            ...connector,
            tasks: getConnectorTasks(connector, tasks),
        }),
    );

    const onDeleteConnectorToken = async (connectorId: string) => {
        try {
            setIsLoading(true);
            await deleteConnectorToken(connectorId);
            syncAndUpdate([], null, false);
        } catch (error) {
            alertError({
                message: translations[language].ERROR_deleting_connector_token,
                traceId: (error as AutomateError).json?.traceId,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ItemList
            title={translations[language].USER_CONNECTIONS_heading}
            hint={translations[language].USER_CONNECTIONS_description}
            toolbarFilters={
                <ExInput
                    data-testId="connectionsSearchInput"
                    className="connections-search exo-search-input"
                    placeholder={translations[language].COMMON_search_placeholder}
                    input-id="default"
                    type="text"
                    footertype="info"
                    inputmode="text"
                    size="default"
                    flavor="default"
                    leadingicon="magnifying-glass"
                    removemarginbottom
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.currentTarget.value)
                    }
                />
            }
            items={authenticatedConnectorsWithTasks}
            itemContent={(connector) => (
                <AuthenticatedConnector
                    connector={connector}
                    onDelete={onDeleteConnectorToken}
                    key={connector.id}
                />
            )}
            emptyStateText={
                search
                    ? translations[language].COMMON_search_hint
                    : translations[language].USER_CONNECTIONS_no_items
            }
        />
    );
};

export default Connections;
